/**
 * AITutorLauncher — the SINGLE tutor UI mounted once at the App level.
 *
 * There is exactly one instance of this component in the whole app tree
 * (mounted in App.js right inside AuthProvider). Every page shares it.
 * Scope, open-state, and message history all live in TutorScopeContext,
 * so this component is stateless w.r.t. cross-page continuity.
 *
 * States:
 *   • bubble  — floating draggable ● pill (right-bottom by default)
 *   • docked  — right-side vertical pane (resize handle on left edge)
 *   • float   — draggable window
 *
 * Voice: TTSSessionToggle (per-sentence TTS enqueue) + MicRecorder
 * (click-to-talk + hands-free auto-listen after tutor stops speaking).
 * Both wired the exact same way as the legacy ChatPanel — this component
 * REPLACES the standalone ChatPanel bubble across the app.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTutorScope } from '../context/TutorScopeContext';
import TTSSessionToggle from './TTSSessionToggle';
import MicRecorder from './MicRecorder';

const C = {
  navy: '#0E2A47', gold: '#FCD359', goldDark: '#E0B73D',
  ink: '#101828', rule: '#E4E7EC',
};

const TUTOR_W_KEY = 'console.tutorWidth';
const MIN_SENTENCE_CHARS = 12;
const TTS_CHUNK_TARGET_CHARS = 220;

function splitSentences(buf) {
  const sentences = [];
  const re = /[^.?!\n]*?[.?!]+["')\]]?(?=\s|$)/g;
  let lastIdx = 0;
  let m;
  while ((m = re.exec(buf)) !== null) {
    const end = re.lastIndex;
    const candidate = buf.slice(lastIdx, end).trim();
    if (candidate.length >= MIN_SENTENCE_CHARS) {
      sentences.push(candidate);
      lastIdx = end;
    }
  }
  const chunks = [];
  let pending = '';
  for (const s of sentences) {
    pending = pending ? `${pending} ${s}` : s;
    if (pending.length >= TTS_CHUNK_TARGET_CHARS) {
      chunks.push(pending);
      pending = '';
    }
  }
  const remainder = (pending ? `${pending} ` : '') + buf.slice(lastIdx);
  return { chunks, remainder };
}

const tinyBtn = {
  background: 'transparent', border: '1px solid #2A4A6E', color: '#E8EEF6',
  width: 24, height: 22, borderRadius: 4, cursor: 'pointer', fontSize: 12,
};

// ── Header shared by docked + floating ─────────────────────────────────
function TutorHeader({ currentModule, currentTopic, onDock, onFloat, onClose }) {
  const scopeText = currentModule?.code
    ? `${currentModule.code}${currentTopic ? ` · ${String(currentTopic.name || currentTopic.title || '').slice(0, 40)}` : ''}`
    : (currentTopic ? String(currentTopic.name || currentTopic.title || '').slice(0, 40) : '—');
  return (
    <div style={{ padding: '9px 12px', borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontFamily: '"Oswald", sans-serif', color: C.gold, letterSpacing: '0.06em', fontSize: 12, fontWeight: 700 }}>AI TUTOR</span>
      <span style={{ fontSize: 10, background: 'rgba(255,255,255,0.06)', color: '#9FB2CB', padding: '2px 6px', borderRadius: 3, letterSpacing: '0.05em' }} data-testid="tutor-scope">
        {scopeText}
      </span>
      <div style={{ flex: 1 }} />
      {onDock && <button onClick={onDock} title="Dock right" style={tinyBtn}>⇥</button>}
      {onFloat && <button onClick={onFloat} title="Float" style={tinyBtn}>⛶</button>}
      <button onClick={onClose} title="Collapse to bubble" style={tinyBtn}>−</button>
    </div>
  );
}

// ── The conversation body (input + messages + voice) ───────────────────
function TutorConvo() {
  const {
    currentModule, currentTopic,
    messages, setMessages,
  } = useTutorScope();

  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const sessionIdRef = useRef(null);
  if (!sessionIdRef.current) {
    sessionIdRef.current = `app-tutor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }
  useEffect(() => { scrollRef.current?.scrollTo(0, 999999); }, [messages]);

  const ttsRef = useRef(null);
  const [autoListenSignal, setAutoListenSignal] = useState(0);
  const onSpeechEnded = () => { setAutoListenSignal((n) => n + 1); };

  const runSend = async (q) => {
    if (!q || streaming) return;
    setDraft('');
    setMessages((prev) => [...prev, { role: 'user', text: q }, { role: 'tutor', text: '', streaming: true }]);
    setStreaming(true);
    const token = localStorage.getItem('token') || '';
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const voice = !!ttsRef.current?.isOn?.();
    const payload = {
      message: q,
      session_id: sessionIdRef.current,
      module_id: currentModule?.id || null,
      topic_id: currentTopic?.id || null,
      scope: currentTopic ? 'topic' : (currentModule ? 'module' : 'course'),
      voice_mode: voice,
    };
    if (voice && ttsRef.current) ttsRef.current.reset();
    let sentenceBuffer = '';
    let streamOk = false;
    try {
      const resp = await fetch(`${API_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify(payload),
      });
      if (resp.ok && resp.body) {
        streamOk = true;
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = '', finalReply = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split('\n\n'); buf = parts.pop() || '';
          for (const block of parts) {
            const line = block.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let evt; try { evt = JSON.parse(line.slice(5).trim()); } catch { continue; }
            if (evt.type === 'delta' && evt.text) {
              finalReply += evt.text;
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (!last || last.role !== 'tutor') return prev;
                return [...prev.slice(0, -1), { ...last, text: last.text + evt.text }];
              });
              if (voice && ttsRef.current) {
                sentenceBuffer += evt.text;
                const { chunks, remainder } = splitSentences(sentenceBuffer);
                for (const c of chunks) ttsRef.current.enqueue(c);
                sentenceBuffer = remainder;
              }
            } else if (evt.type === 'done') {
              setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (!last || last.role !== 'tutor') return prev;
                return [...prev.slice(0, -1), { ...last, text: evt.reply || finalReply || last.text, streaming: false }];
              });
              if (voice && ttsRef.current) {
                const tail = sentenceBuffer.trim();
                if (tail.length >= 2) ttsRef.current.enqueue(tail);
                sentenceBuffer = '';
                ttsRef.current.finalize();
              }
            }
          }
        }
      }
    } catch (_) { /* fall through to sync */ }
    if (!streamOk) {
      try {
        const { data } = await axios.post(`${API_URL}/api/chat`, payload, {
          headers: { Authorization: token ? `Bearer ${token}` : '' },
          timeout: 60000,
        });
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (!last || last.role !== 'tutor') return prev;
          return [...prev.slice(0, -1), { ...last, text: data.reply || '(no reply)', streaming: false }];
        });
        if (voice && ttsRef.current && data.reply) {
          ttsRef.current.reset();
          ttsRef.current.enqueue(data.reply);
          ttsRef.current.finalize();
        }
      } catch (e) {
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (!last || last.role !== 'tutor') return prev;
          return [...prev.slice(0, -1), { ...last, text: `Sorry — ${e?.response?.data?.detail || e?.message || 'chat failed'}`, streaming: false }];
        });
        if (voice && ttsRef.current) ttsRef.current.reset();
      }
    }
    setStreaming(false);
  };

  // If the drawer / any page pushes an `askSeed` (a user message with
  // pending:true), fire it as a real /api/chat send now.
  useEffect(() => {
    const idx = messages.findIndex((m) => m.role === 'user' && m.pending);
    if (idx === -1 || streaming) return;
    const seed = messages[idx];
    // Remove the placeholder so runSend can re-append it cleanly.
    setMessages((prev) => prev.filter((_, i) => i !== idx));
    runSend(seed.text);
  }, [messages, streaming]);

  const send = () => { runSend(draft.trim()); };

  const onTranscript = (text, opts = {}) => {
    const clean = (text || '').trim();
    if (!clean) return;
    if (opts.autoSend) runSend(clean);
    else setDraft((d) => (d ? `${d} ${clean}` : clean));
  };

  return (
    <>
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.length === 0 && (
          <div style={{ color: '#9FB2CB', fontSize: 13, lineHeight: 1.5 }}>
            Ask about <strong style={{ color: C.gold }}>{currentTopic?.name || currentTopic?.title || 'the current module'}</strong>. Answers stream from <code>/api/chat/stream</code>, scoped to whichever module/topic is active.
            <div style={{ marginTop: 8, fontSize: 11, color: '#7F94B0' }}>
              Tap <span style={{ color: C.gold }}>Voice</span> to have the tutor read replies aloud, then <span style={{ color: C.gold }}>🎤</span> to speak your next question hands-free.
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '82%', background: m.role === 'user' ? 'rgba(252,211,89,0.16)' : 'rgba(255,255,255,0.05)', color: '#E8EEF6', border: `1px solid ${m.role === 'user' ? 'rgba(252,211,89,0.3)' : 'rgba(255,255,255,0.08)'}`, padding: '7px 10px', borderRadius: 10, fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {m.text}{m.streaming && <span style={{ opacity: 0.6 }}> ▍</span>}
          </div>
        ))}
      </div>
      <div style={{ padding: 8, borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 6, alignItems: 'stretch', flexWrap: 'wrap' }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
          placeholder={streaming ? 'Streaming…' : 'Ask a question…'}
          disabled={streaming}
          data-testid="tutor-input"
          style={{ flex: '1 1 160px', minWidth: 140, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#E8EEF6', padding: '7px 9px', fontSize: 12.5, outline: 'none', opacity: streaming ? 0.6 : 1 }} />
        <MicRecorder
          onTranscript={onTranscript}
          disabled={streaming}
          autoListenSignal={autoListenSignal}
          testId="tutor-mic-btn"
        />
        <TTSSessionToggle
          ref={ttsRef}
          onSpeechEnded={onSpeechEnded}
          testId="tutor-tts-toggle"
        />
        <button onClick={send} disabled={streaming || !draft.trim()}
          data-testid="tutor-send-btn"
          style={{ background: C.gold, color: C.navy, border: 'none', padding: '7px 11px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: streaming ? 'not-allowed' : 'pointer', opacity: streaming ? 0.5 : 1 }}>
          Send
        </button>
      </div>
    </>
  );
}

// ── Docked pane (right side, resizable) ────────────────────────────────
function TutorDocked({ onFloat, onClose }) {
  const { currentModule, currentTopic, tutorPaneW, setTutorPaneW } = useTutorScope();
  const dragRef = useRef({ dragging: false, startX: 0, startW: 0 });
  const onDown = (e) => {
    dragRef.current = { dragging: true, startX: e.clientX, startW: tutorPaneW };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    e.preventDefault();
  };
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.dragging) return;
      const delta = d.startX - e.clientX;
      const next = Math.min(900, Math.max(320, d.startW + delta));
      setTutorPaneW(next);
      try { window.localStorage.setItem(TUTOR_W_KEY, String(next)); } catch { /* ignore */ }
    };
    const onUp = () => {
      if (dragRef.current.dragging) {
        dragRef.current.dragging = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [setTutorPaneW]);
  return (
    <aside data-testid="tutor-pane-docked"
      style={{ position: 'fixed', top: 60, right: 0, width: tutorPaneW, minWidth: 320, height: 'calc(100vh - 60px)', borderLeft: `1px solid ${C.rule}`, background: C.navy, color: '#E8EEF6', display: 'flex', flexDirection: 'column', zIndex: 55 }}>
      <div onMouseDown={onDown} onDoubleClick={() => { setTutorPaneW(460); try { window.localStorage.setItem(TUTOR_W_KEY, '460'); } catch { /* ignore */ } }}
        data-testid="tutor-resize-knob"
        title="Drag to resize · double-click to reset"
        style={{ position: 'absolute', left: -3, top: 0, bottom: 0, width: 7, cursor: 'col-resize', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 5, height: 36, background: C.gold, borderRadius: 3, border: `1px solid ${C.goldDark}`, opacity: 0.85, boxShadow: '0 2px 6px rgba(14,42,71,0.4)' }} />
      </div>
      <TutorHeader currentModule={currentModule} currentTopic={currentTopic} onFloat={onFloat} onClose={onClose} />
      <TutorConvo />
    </aside>
  );
}

// ── Floating window ─────────────────────────────────────────────────────
function TutorFloating({ onDock, onClose }) {
  const { currentModule, currentTopic } = useTutorScope();
  const [pos, setPos] = useState({ x: Math.max(40, (typeof window !== 'undefined' ? window.innerWidth : 1024) - 480), y: 120 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const onDown = (e) => { dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: pos.x, origY: pos.y }; };
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d.dragging) return;
      setPos({ x: Math.max(8, d.origX + (e.clientX - d.startX)), y: Math.max(8, d.origY + (e.clientY - d.startY)) });
    };
    const onUp = () => { dragRef.current.dragging = false; };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);
  return (
    <div data-testid="tutor-pane-floating"
      style={{ position: 'fixed', left: pos.x, top: pos.y, width: 420, height: 500, background: C.navy, color: '#E8EEF6', borderRadius: 10, boxShadow: '0 20px 60px rgba(14,42,71,0.55)', border: `1px solid ${C.goldDark}`, zIndex: 60, display: 'flex', flexDirection: 'column' }}>
      <div onMouseDown={onDown} style={{ cursor: 'move' }}>
        <TutorHeader currentModule={currentModule} currentTopic={currentTopic} onDock={onDock} onClose={onClose} />
      </div>
      <TutorConvo />
    </div>
  );
}

// ── Floating bubble (collapsed state) ──────────────────────────────────
function TutorBubble({ onOpen }) {
  const { bubblePos, setBubblePos } = useTutorScope();
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0, moved: false });
  const onDown = (e) => { dragRef.current = { dragging: true, startX: e.clientX, startY: e.clientY, origX: bubblePos.x, origY: bubblePos.y, moved: false }; };
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current; if (!d.dragging) return;
      const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
      setBubblePos({ x: Math.max(8, d.origX - dx), y: Math.max(8, d.origY - dy) });
    };
    const onUp = () => { dragRef.current.dragging = false; };
    window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [setBubblePos]);
  return (
    <button data-testid="tutor-bubble"
      onMouseDown={onDown}
      onClick={() => { if (!dragRef.current.moved) onOpen(); }}
      title="Ask the AI Tutor  (drag to move · click to open)"
      style={{ position: 'fixed', right: bubblePos.x, bottom: bubblePos.y, zIndex: 90, width: 52, height: 52, borderRadius: '50%', background: C.navy, color: C.gold, border: `2px solid ${C.goldDark}`, boxShadow: '0 12px 32px rgba(14,42,71,0.45)', cursor: 'grab', display: 'grid', placeItems: 'center', fontSize: 20 }}>
      💬
    </button>
  );
}

/**
 * The one and only tutor UI in the app. Mount this once in App.js inside
 * the auth provider — every page automatically gets access to it.
 */
export default function AITutorLauncher() {
  const { user } = useAuth();
  const { tutorState, setTutorState } = useTutorScope();

  // Cmd/Ctrl+J toggles the docked pane from anywhere.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault();
        setTutorState(tutorState === 'docked' ? 'bubble' : 'docked');
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [tutorState, setTutorState]);

  // Hide entirely on unauthenticated pages (Login / Register).
  if (!user) return null;

  if (tutorState === 'docked') {
    return (
      <TutorDocked
        onFloat={() => setTutorState('float')}
        onClose={() => setTutorState('bubble')}
      />
    );
  }
  if (tutorState === 'float') {
    return (
      <TutorFloating
        onDock={() => setTutorState('docked')}
        onClose={() => setTutorState('bubble')}
      />
    );
  }
  return <TutorBubble onOpen={() => setTutorState('docked')} />;
}
